// The responsive box every kit chart renders into — plus the phone affordances
// from the mobile pass (docs/design/DESIGN_SYSTEM.md → "Mobile" → charts):
//   • height = authored px × --pb-chart-scale, with a 200 px floor on phones
//     (tokens.css .pb-chart-frame), so a 120 px desktop strip stays readable;
//   • an Expand affordance (hover-revealed with a fine pointer, always shown
//     under a coarse one) and tap-to-fullscreen on coarse pointers, opening the
//     same chart element in ChartFullscreen with a fixed readout bar. Charts
//     that own a click (block drill-down) keep taps for themselves;
//   • the heading comes from `title`, else the enclosing Card's title.
// `debounce` coalesces resize-driven re-measures; memo() lets a stable child
// element skip re-render.

import { memo, useCallback, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement } from "react";
import { Maximize2 } from "lucide-react";
import { ResponsiveContainer } from "recharts";

import { useCardTitle } from "@/app/console/cardContext";
import { usePointerCoarse } from "@/app/console/useViewport";
import { ChartFullscreen } from "@/components/charts/ChartFullscreen";

export interface ChartFrameProps {
  height?: number;
  children: ReactElement;
  /** Heading of the full-screen view and the Expand label; defaults to the enclosing Card's title. */
  title?: string;
  /** Expand affordance + tap-to-fullscreen (default on). Off for decorative strips. */
  expandable?: boolean;
}

const TAP_MS = 350;
const TAP_PX = 8;

export const ChartFrame = memo(function ChartFrame({ height = 220, children, title, expandable = true }: ChartFrameProps) {
  const cardTitle = useCardTitle();
  const heading = title ?? cardTitle ?? "Chart";
  const [open, setOpen] = useState(false);
  const coarse = usePointerCoarse();
  const tap = useRef<{ x: number; y: number; t: number } | null>(null);
  const hasOwnClick = Boolean((children.props as { onClick?: unknown }).onClick);
  const tapToExpand = expandable && coarse && !hasOwnClick;

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!tapToExpand || (e.target as HTMLElement).closest("button")) return;
      tap.current = { x: e.clientX, y: e.clientY, t: performance.now() };
    },
    [tapToExpand],
  );
  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const t = tap.current;
      tap.current = null;
      if (!t || !tapToExpand) return;
      if (performance.now() - t.t > TAP_MS) return; // a hold = scrub in place
      if (Math.hypot(e.clientX - t.x, e.clientY - t.y) > TAP_PX) return; // a drag = scrub
      setOpen(true);
    },
    [tapToExpand],
  );
  const close = useCallback(() => setOpen(false), []);

  return (
    <div
      className="pb-chart-frame group"
      style={{ "--pb-chart-h": `${height}px` } as CSSProperties}
      data-chart-frame=""
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        tap.current = null;
      }}
    >
      <ResponsiveContainer width="100%" height="100%" debounce={60}>
        {children}
      </ResponsiveContainer>
      {expandable && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Expand chart: ${heading}`}
          title="Expand"
          className="absolute -right-1 -top-1 z-[1] flex h-8 w-8 items-center justify-center text-text-3 opacity-0 transition-opacity duration-fast hover:text-text-1 focus-visible:opacity-100 focus-visible:outline-none group-hover:opacity-100 coarse:h-11 coarse:w-11 coarse:opacity-100"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-[6px] bg-surface-1/90 ring-1 ring-line transition-colors duration-fast group-hover:ring-line-strong" aria-hidden="true">
            <Maximize2 className="h-3.5 w-3.5" />
          </span>
        </button>
      )}
      {expandable && (
        <ChartFullscreen open={open} onClose={close} title={heading}>
          {children}
        </ChartFullscreen>
      )}
    </div>
  );
});
